// Shared feedback modal — optional thumbs rating, free-text message, optional
// reply email, and a direct mailto escape hatch. Used from the Home footer
// (source: 'footer'), the Game Board quiet link (source: 'game_board'), and
// the how-to page Contact link (source: 'contact'). Contact is the same
// submitFeedback write to feedback/{pushId}. Contact hides thumbs and the
// intro, uses placeholders "Your message" / "Email (optional)", and always
// sends rating: null. Footer and game-board copy stay unchanged.
// Local component state only: opening/closing it never touches Firebase room
// state, so a host dealing the next hand can't rip it away mid-typing.
import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { t } from '../i18n';
import { localizeError } from '../i18n/errors';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { submitFeedback } from '../utils/feedback';

const fieldLabel = 'label-eyebrow mb-2';
const fieldInput =
  'w-full px-4 py-3 bg-transparent border border-line rounded-lg text-ink placeholder:text-mute focus:outline-none focus:border-ink transition-colors';

function FeedbackModal({
  onClose,
  source = 'footer',
  title = t('feedback.title'),
  intro = t('feedback.intro'),
  placeholder = t('feedback.placeholder'),
  thanks = t('feedback.thanks'),
}) {
  const [fbRating, setFbRating] = useState(null);
  const [fbMessage, setFbMessage] = useState('');
  const [fbEmail, setFbEmail] = useState('');
  const [fbSubmitting, setFbSubmitting] = useState(false);
  const [fbError, setFbError] = useState('');
  const [fbSubmitted, setFbSubmitted] = useState(false);
  const isContact = source === 'contact';

  // Rating OR message makes a valid submission — a thumb alone is enough.
  // Contact hides thumbs, so a message is required.
  const canSend = isContact ? !!fbMessage.trim() : !!fbRating || !!fbMessage.trim();

  // Toggle: tapping the selected thumb clears it. Selection is part of the
  // form, NOT an instant submit — everything goes as ONE entry on Send, so a
  // rate-and-write never produces duplicate entries (unlike the end-game
  // card, whose thumb tap is deliberately its own immediate submission).
  const toggleRating = (value) => {
    if (fbSubmitting) return;
    setFbRating((prev) => (prev === value ? null : value));
  };

  const handleFeedbackSubmit = async (e) => {
    e?.preventDefault();
    if (fbSubmitting || !canSend) return;
    setFbSubmitting(true);
    setFbError('');
    try {
      await submitFeedback({
        rating: isContact ? null : fbRating,
        message: fbMessage,
        email: fbEmail,
        context: { source },
      });
      setFbSubmitted(true);
    } catch (err) {
      setFbError(localizeError(err?.message || 'Something went wrong. Please try again.'));
    } finally {
      setFbSubmitting(false);
    }
  };

  const thumbClass = (selected) =>
    `w-12 h-12 rounded-full border flex items-center justify-center transition-colors ${
      selected
        ? 'bg-ink text-cream border-ink'
        : 'bg-transparent text-ink border-line hover:border-ink'
    }`;

  return (
      <Modal
        open
        onClose={onClose}
        title={title}
        footer={
          fbSubmitted ? null : (
            <Button
              variant="primary"
              size="sm"
              onClick={handleFeedbackSubmit}
              disabled={fbSubmitting || !canSend}
              trailingArrow
            >
              {fbSubmitting ? t('common.sending') : t('common.send')}
            </Button>
          )
        }
      >
        {fbSubmitted ? (
          <div className="space-y-3 text-ink-2 py-2">
            <p className="text-[17px] text-ink font-medium">{t('feedback.thankYou')}</p>
            <p className="text-[14px]">{thanks}</p>
          </div>
        ) : (
          <form onSubmit={handleFeedbackSubmit} className="space-y-4">
            {!isContact && (
              <p className="text-[14px] text-ink-2">{intro}</p>
            )}

            {!isContact && (
              <div className="flex items-center justify-between gap-4">
                <div className={`${fieldLabel} mb-0`}>{t('feedback.hows')}</div>
                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => toggleRating('up')}
                    aria-label={t('settle.thumbsUp')}
                    aria-pressed={fbRating === 'up'}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    className={thumbClass(fbRating === 'up')}
                  >
                    <ThumbsUp size={20} strokeWidth={1.75} />
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleRating('down')}
                    aria-label={t('settle.thumbsDown')}
                    aria-pressed={fbRating === 'down'}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    className={thumbClass(fbRating === 'down')}
                  >
                    <ThumbsDown size={20} strokeWidth={1.75} />
                  </button>
                </div>
              </div>
            )}

            <div>
              {!isContact && <div className={fieldLabel}>{t('feedback.yourMessage')}</div>}
              {/* No autoFocus — the keyboard must not shove itself over the
                  modal on open; thumb-only feedback never needs it at all. */}
              <textarea
                value={fbMessage}
                onChange={(e) => setFbMessage(e.target.value)}
                placeholder={isContact ? t('feedback.yourMessage') : placeholder}
                aria-label={isContact ? t('feedback.yourMessage') : undefined}
                className={`${fieldInput} min-h-[120px] resize-y`}
                maxLength={2000}
                disabled={fbSubmitting}
              />
            </div>

            <div>
              {!isContact && <div className={fieldLabel}>{t('feedback.emailOptional')}</div>}
              <input
                type="email"
                value={fbEmail}
                onChange={(e) => setFbEmail(e.target.value)}
                placeholder={isContact ? t('feedback.emailOptional') : t('feedback.emailPlaceholder')}
                aria-label={isContact ? t('feedback.emailOptional') : undefined}
                className={fieldInput}
                maxLength={254}
                disabled={fbSubmitting}
              />
              {!isContact && (
                <div className="text-[12px] text-mute mt-1.5">
                  {t('feedback.replyHint')}
                </div>
              )}
            </div>

            {fbError && (
              <div className="text-[13px] text-accent-danger border border-accent-danger/30 rounded-lg px-3 py-2">
                {fbError}
              </div>
            )}

            <div className="pt-1 border-t border-line-2 text-[13px] text-mute">
              {t('feedback.orEmailUs')}{' '}
              <a
                href="mailto:playchipless@outlook.com"
                className="text-ink underline underline-offset-2 decoration-line hover:decoration-ink transition-colors break-all"
              >
                playchipless@outlook.com
              </a>
            </div>

            <button type="submit" hidden disabled={fbSubmitting || !canSend} />
          </form>
        )}
      </Modal>
  );
}

export default FeedbackModal;
